import { ProductGridRenderer } from '@/features/shop/components/ProductGrid/ProductGridRenderer'
import { ShelvesRenderer } from '@/features/shop/components/Shelf/ShelvesRenderer'
import { SpecialRequestGridCard } from '@/features/shop/components/SpecialRequestGridCard'
import { LoadingIndicator } from '@shipt/design-system-loading'
import { type useInfiniteQuerySearch } from '@/features/shop/services/Search/queries'
import { useInfiniteLoadingGrid } from '@/features/shop/components/ProductGrid/useInfiniteLoadingGrid'
import { Box } from '@shipt/design-system-layouts'
import { type AdShowcaseData, type AdVideoShowcaseData } from '@/features/cms/components/CMS/types'
import { BUY_AGAIN_SINGLE_RETAILER_TYPE } from '@/features/shared/constants/shelves'
import { getBuyAgainShelf } from '@/features/shop/services/Search/utils'
import { routes } from '@shared/constants/routes'
import { FeedbackBanner } from '@shipt/design-system-banner'
import { usePathWithSearch } from '@/features/shared/hooks/routes/usePathWithSearch'
import { useUser } from '@/features/account/services/User/hooks'

type Props = {
  infiniteQueryResults: ReturnType<typeof useInfiniteQuerySearch>
  showLargerProducts?: boolean
  showAddToListButton?: boolean
  isSearchGrid?: boolean
  adShowcase?: AdShowcaseData | AdVideoShowcaseData
  showLoginBanner?: boolean
}

export const SearchProductsRenderer = ({
  infiniteQueryResults,
  adShowcase,
  showLargerProducts,
  showAddToListButton,
  isSearchGrid,
  showLoginBanner = false,
}: Props) => {
  const { searchResults, hasNextPage, isFetchingNextPage, isFetched } = infiniteQueryResults
  const { shelves, products, page } = searchResults
  const { handleSetNextPage } = useInfiniteLoadingGrid({
    ...infiniteQueryResults,
    page,
  })
  const user = useUser()
  const buyAgainShelf = shelves.filter((shelf) => shelf.type === BUY_AGAIN_SINGLE_RETAILER_TYPE)
  const showBuyAgainShelf = Boolean(isFetched && !!getBuyAgainShelf(shelves))

  const currentUrl = usePathWithSearch()
  const loginUrl = `${routes.LOGIN.url}?returnTo=${encodeURIComponent(currentUrl)}`

  if (shelves.length > 0 && !buyAgainShelf.length) {
    return <ShelvesRenderer shelves={shelves} />
  }

  return products?.length ? (
    <>
      <ProductGridRenderer
        searchResults={searchResults}
        setNextPage={handleSetNextPage}
        adShowcase={adShowcase}
        className="searchProductsRender"
        showLargerProducts={showLargerProducts}
        showAddToListButton={showAddToListButton}
        isSearchGrid={isSearchGrid}
        showBuyAgainShelf={showBuyAgainShelf}
      />
      {!!showLoginBanner && (
        <FeedbackBanner
          hasIcon
          variant="warning"
          title="Still looking for what you need? Sign in to continue shopping."
          actionButton={{
            content: 'Sign In',
            forwardedAs: 'a',
            href: loginUrl,
          }}
        />
      )}
      {!!hasNextPage && (
        <Box margin={['3xl', 'auto']}>{!!isFetchingNextPage && <LoadingIndicator size="xl" />}</Box>
      )}
      {!!user && <SpecialRequestGridCard />}
    </>
  ) : null
}
